import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material';

import type { MakerFlag } from '../../types/entry.types';
import { TAB_FLAGS } from './entryMath';
import { useBulkSetMakerFlag } from '../../api/useEntry';
import {
  applyBulkMakerFlagDtos,
  buildBulkMakerFlagDtos,
  type BulkMakerSelection
} from './bulkScope';

/**
 * LIVE one-click per-leg maker-flag toggles for the active tab's path — Poly
 * maker + Kalshi maker, each with ON / OFF. Mirrors the per-row maker switches
 * (Ctrl P / K) but flips the flag across a WHOLE scope in one request via
 * `/rules/bulk-maker-flag`. Across both Path tabs the operator reaches all four
 * flags. This is NOT the subgroup "Apply snapshot" flow — it is a direct live
 * flag flip, kept deliberately separate.
 *
 * The control is scope-aware: the same UI drives a GROUP scope (following the
 * page's group filter exactly like "All ON/OFF": no filter → scope:'all'; a
 * real group → scope:'all' + groupId; Ungrouped → scope:'ids' over the
 * group-scoped rule ids) OR a concrete SUBGROUP scope (scope:'all' +
 * subgroupId). ON may ACTIVATE paused rules, so every flip routes through a
 * confirm dialog; OFF does too (it can auto-pause when the last flag clears).
 */

export type GroupMakerScope =
  | {
      kind: 'group';
      /** The page's group filter — all / a real group / ungrouped. */
      selection: BulkMakerSelection;
      label: string | null;
      /** Group-scoped (pre-search) rule ids — only used on the Ungrouped path. */
      scopedRuleIds: string[];
    }
  | {
      kind: 'subgroup';
      subgroupId: string;
      label: string;
    };

interface GroupMakerBulkControlsProps {
  path: 'a' | 'b';
  scope: GroupMakerScope;
  /** Honest pair total for the confirm dialog (the whole scope, not just the
   *  search-narrowed rows). */
  confirmCount: number;
  /** Rule ids behind the currently checked rows. When `selectedCount > 0` the
   *  control switches to SELECTION mode (scope:'ids' over these ids), exactly
   *  like the market-data "Enable/Disable selected" — group scope is ignored
   *  while a selection is live. */
  selectedRuleIds?: string[];
  selectedCount?: number;
}

export function GroupMakerBulkControls({
  path,
  scope,
  confirmCount,
  selectedRuleIds = [],
  selectedCount = 0
}: GroupMakerBulkControlsProps) {
  const bulkMaker = useBulkSetMakerFlag();
  const [confirm, setConfirm] = useState<{
    flag: MakerFlag;
    value: boolean;
    label: string;
  } | null>(null);

  // Selection mode mirrors BulkActionBar: a live checkbox selection takes
  // over the PM/KM ON/OFF buttons and scopes them to the checked rows only.
  const selectionMode = selectedCount > 0;

  // Short button labels (PM / KM) with a full name kept for the confirm copy.
  const flags: { label: string; full: string; flag: MakerFlag }[] = [
    { label: 'PM', full: 'Poly maker', flag: TAB_FLAGS[path].poly },
    { label: 'KM', full: 'Kalshi maker', flag: TAB_FLAGS[path].kalshi }
  ];

  const labelSx = {
    fontSize: '0.62rem',
    color: 'grey.500',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap' as const
  };
  const btnSx = { fontSize: '0.62rem', py: 0.1, px: 0.8, minWidth: 0 } as const;

  const run = useCallback(
    async (flag: MakerFlag, value: boolean) => {
      const selection: BulkMakerSelection = selectionMode
        ? { kind: 'selected', ruleIds: selectedRuleIds }
        : scope.kind === 'subgroup'
          ? { kind: 'subgroup', subgroupId: scope.subgroupId }
          : scope.selection;
      const scopedRuleIds =
        !selectionMode && scope.kind === 'group' ? scope.scopedRuleIds : [];
      const dtos = buildBulkMakerFlagDtos(selection, flag, value, scopedRuleIds);
      if (dtos.length === 0) return;
      await applyBulkMakerFlagDtos(dtos, dto => bulkMaker.mutateAsync(dto));
    },
    [scope, bulkMaker, selectionMode, selectedRuleIds]
  );

  // Honest count + noun for the confirm dialog — the checked subset when a
  // selection is live, otherwise the whole group/subgroup scope.
  const effectiveCount = selectionMode ? selectedCount : confirmCount;
  const scopeNoun = selectionMode
    ? 'the selected pairs'
    : scope.kind === 'subgroup'
      ? scope.label
      : scope.selection.kind === 'all'
        ? 'ALL groups'
        : scope.label ?? 'this group';

  return (
    <>
      {bulkMaker.isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <CircularProgress size={13} thickness={5} />
          <Typography sx={{ ...labelSx, color: 'grey.300' }}>
            Applying — maker flags…
          </Typography>
        </Box>
      ) : (
        <>
          {selectionMode && (
            <Typography sx={{ ...labelSx, color: 'grey.300' }}>
              {selectedCount} selected
            </Typography>
          )}
          {flags.map(({ label, full, flag }) => (
            <Box
              key={flag}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Typography sx={labelSx}>{label}</Typography>
              <Button
                size="small"
                color="success"
                variant="outlined"
                sx={btnSx}
                disabled={bulkMaker.isLoading}
                onClick={() => setConfirm({ flag, value: true, label: full })}
              >
                ON
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                sx={btnSx}
                disabled={bulkMaker.isLoading}
                onClick={() => setConfirm({ flag, value: false, label: full })}
              >
                OFF
              </Button>
            </Box>
          ))}
        </>
      )}

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        <DialogTitle sx={{ fontSize: '0.95rem' }}>
          {confirm?.value ? 'Enable' : 'Disable'} {confirm?.label}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem' }}>
            This flips {confirm?.label} across {effectiveCount} pair
            {effectiveCount === 1 ? '' : 's'} in {scopeNoun}.
            {confirm?.value
              ? ' Turning it ON may ACTIVATE paused rules and start placing maker orders.'
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            color={confirm?.value ? 'success' : 'error'}
            onClick={() => {
              if (confirm) void run(confirm.flag, confirm.value);
              setConfirm(null);
            }}
            autoFocus
          >
            {confirm?.value ? 'Enable' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
